import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULT_PLATFORM_TZ = 'Indian/Antananarivo';

let cachedTz = null;
let inFlight = null;

/* Fetches the admin-configured platform timezone once and caches it in
   module scope so every admin component sharing this hook stays in sync
   without a Context provider. `setCachedPlatformTimezone` lets the
   Settings page push a fresh value the instant it's saved. */
export const fetchPlatformTimezone = () => {
  if (cachedTz) return Promise.resolve(cachedTz);
  if (!inFlight) {
    inFlight = axios.get(`${API}/admin/platform-config`, { withCredentials: true })
      .then(({ data }) => {
        cachedTz = data.platform_timezone || DEFAULT_PLATFORM_TZ;
        return cachedTz;
      })
      .catch(() => {
        inFlight = null;
        return DEFAULT_PLATFORM_TZ;
      });
  }
  return inFlight;
};

export const setCachedPlatformTimezone = (tz) => {
  cachedTz = tz || DEFAULT_PLATFORM_TZ;
};

export const usePlatformTimezone = () => {
  const [tz, setTz] = useState(cachedTz || DEFAULT_PLATFORM_TZ);
  useEffect(() => {
    fetchPlatformTimezone().then(setTz);
  }, []);
  return tz;
};

/* Offset (in minutes) of `timeZone` from UTC at the instant `date` falls on. */
const getTzOffsetMinutes = (date, timeZone) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return (asUTC - date.getTime()) / 60000;
};

/* Converts a naive "YYYY-MM-DDTHH:mm" (as typed into a <input type="datetime-local">)
   INTERPRETED AS `timeZone` local time, into a UTC ISO string for the backend. */
export const zonedTimeToUtcIso = (localStr, timeZone) => {
  if (!localStr) return null;
  const [datePart, timePart] = localStr.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart || '00:00').split(':').map(Number);
  const utcGuess = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const offsetMinutes = getTzOffsetMinutes(utcGuess, timeZone);
  return new Date(utcGuess.getTime() - offsetMinutes * 60000).toISOString();
};

/* Reverse of the above — converts a stored UTC ISO string into the
   "YYYY-MM-DDTHH:mm" value expected by a datetime-local input, expressed
   in `timeZone` local time (so editing a campaign shows the platform's
   local send time, not the admin's own browser timezone). */
export const utcIsoToZonedInputValue = (isoString, timeZone) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

/* Formats a stored UTC ISO string for display in `timeZone` local time. */
export const formatInTimezone = (isoString, timeZone, options) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    ...options,
  }).format(date);
};

/* Standard IANA timezone list — sourced from the browser's own ICU data
   (all modern browsers support this) so we never hand-maintain a stale list. */
export const getStandardTimezones = () => {
  try {
    const zones = Intl.supportedValuesOf('timeZone');
    return zones.includes('UTC') ? zones : ['UTC', ...zones];
  } catch {
    return [DEFAULT_PLATFORM_TZ, 'UTC', 'Europe/Paris', 'America/New_York'];
  }
};
